import { CommandMenu, IconButton, LuIcon, Text } from "@overmap-ai/blocks"
import { UNLABELLED_ASSET_LABEL, UNTITLED_ISSUE_TITLE } from "@overmap-ai/common"
import type { Asset, FormSubmission, Issue, Stored } from "@overmap-ai/common"
import { useScanner } from "@overmap-ai/forms"
import { memo, type ReactElement, startTransition, useCallback, useMemo, useState } from "react"

import { ASSET_FUSE_OPTIONS } from "../../fuse/assets"
import { FORM_SUBMISSION_FUSE_OPTIONS } from "../../fuse/forms"
import { ISSUE_FUSE_OPTIONS } from "../../fuse/issues"
import { useChangeEventHandler, useFuse } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import type { AssetAndIdentifiers, FormSubmissionAndIdentifiers, IssueAndIdentifiers } from "../../typings"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { IssueTypeIcon } from "../Icons/IssueTypeIcon"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import { FormSubmissionIndexLabel } from "../IndexLabels/FormSubmissionIndexLabel"
import { IssueIndexLabel } from "../IndexLabels/IssueIndex"
import type { ExtendableCommandMenuProps } from "./typings"

interface ProjectSearchCommandMenuProps extends Omit<ExtendableCommandMenuProps, "title"> {
	children?: ReactElement
	onSelectAsset?: (asset: Stored<Asset>) => void
	onSelectIssue?: (issue: Stored<Issue>) => void
	onSelectFormSubmission?: (formSubmission: Stored<FormSubmission>) => void
	assetAndIdentifiers: AssetAndIdentifiers[]
	issuesAndIdentifiers: IssueAndIdentifiers[]
	formSubmissionsAndIdentifiers: FormSubmissionAndIdentifiers[]
}

export const ProjectSearchCommandMenu = memo<ProjectSearchCommandMenuProps>((props) => {
	const {
		children,
		open,
		defaultOpen,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		assetAndIdentifiers,
		issuesAndIdentifiers,
		formSubmissionsAndIdentifiers,
		onSelectAsset,
		onSelectIssue,
		onSelectFormSubmission,
		...rest
	} = props

	const openScanner = useScanner()
	const [searchTerm, setSearchTerm] = useState("")

	const filteredAssets = useFuse<AssetAndIdentifiers>(assetAndIdentifiers, searchTerm, ASSET_FUSE_OPTIONS)
	const filteredIssues = useFuse<IssueAndIdentifiers>(issuesAndIdentifiers, searchTerm, ISSUE_FUSE_OPTIONS)
	const filteredFormSubmissions = useFuse<FormSubmissionAndIdentifiers>(
		formSubmissionsAndIdentifiers,
		searchTerm,
		FORM_SUBMISSION_FUSE_OPTIONS,
	)

	const search = useCallback((value: string) => {
		startTransition(() => {
			setSearchTerm(value)
		})
	}, [])

	const handleScan = useCallback(() => {
		openScanner((close) => ({
			onDecodeResult: (result) => {
				search(result.rawValue.trim())
				close()
			},
			onClose: close,
		}))
	}, [openScanner, search])

	const assetsLimited = useMemo(() => {
		return filteredAssets.slice(0, 10)
	}, [filteredAssets])

	const issuesLimited = useMemo(() => {
		return filteredIssues.slice(0, 10)
	}, [filteredIssues])

	const formSubmissionsLimited = useMemo(() => {
		return filteredFormSubmissions.slice(0, 10)
	}, [filteredFormSubmissions])

	const isEmpty = assetsLimited.length === 0 && issuesLimited.length === 0 && formSubmissionsLimited.length === 0

	return (
		<CommandMenu.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
			{!!children && <CommandMenu.Trigger asChild>{children}</CommandMenu.Trigger>}
			<CommandMenu.Overlay />
			<CommandMenu.Content title="Project search command menu" {...rest}>
				<div className="flex items-center justify-between gap-2">
					<CommandMenu.Input
						value={searchTerm}
						placeholder="Search"
						onChange={useChangeEventHandler(search)}
					/>
					<IconButton onClick={handleScan} variant="soft" className="mr-2">
						<LuIcon icon={modelIconMapping.scan} />
					</IconButton>
				</div>
				<CommandMenu.Separator />
				<CommandMenu.Scroll>
					<CommandMenu.Group>
						{issuesLimited.length > 0 ? <CommandMenu.GroupLabel>Issues</CommandMenu.GroupLabel> : null}
						{issuesLimited.map(({ issue }) => {
							return (
								<CommandMenu.Item key={issue.uuid} onSelect={() => onSelectIssue?.(issue)}>
									<IssueTypeIcon issueTypeId={issue.issue_type} />
									<IssueIndexLabel issueId={issue.uuid} />
									<Text className="truncate" accentColor="base">
										{issue.title || UNTITLED_ISSUE_TITLE}
									</Text>
								</CommandMenu.Item>
							)
						})}
					</CommandMenu.Group>

					<CommandMenu.Group>
						{assetsLimited.length > 0 ? <CommandMenu.GroupLabel>Assets</CommandMenu.GroupLabel> : null}
						{assetsLimited.map(({ asset }) => {
							return (
								<CommandMenu.Item key={asset.uuid} onSelect={() => onSelectAsset?.(asset)}>
									<AssetTypeIcon assetTypeId={asset.asset_type} />
									<AssetIndexLabel assetId={asset.uuid} />
									<Text className="truncate" accentColor="base">
										{asset.label || UNLABELLED_ASSET_LABEL}
									</Text>
								</CommandMenu.Item>
							)
						})}
					</CommandMenu.Group>

					<CommandMenu.Group>
						{formSubmissionsLimited.length > 0 ? (
							<CommandMenu.GroupLabel>Form submissions</CommandMenu.GroupLabel>
						) : null}
						{formSubmissionsLimited.map(({ formSubmission }) => {
							return (
								<CommandMenu.Item
									key={formSubmission.uuid}
									onSelect={() => onSelectFormSubmission?.(formSubmission)}
								>
									<LuIcon icon={modelIconMapping.form} />
									<FormSubmissionIndexLabel formSubmissionId={formSubmission.uuid} />
								</CommandMenu.Item>
							)
						})}
					</CommandMenu.Group>

					{isEmpty ? <CommandMenu.Empty>No results.</CommandMenu.Empty> : null}
				</CommandMenu.Scroll>
			</CommandMenu.Content>
		</CommandMenu.Root>
	)
})
ProjectSearchCommandMenu.displayName = "ProjectCommandMenu"
